import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import { UseFormReturn } from 'react-hook-form';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentText } from '@models/Forms/IForms';
import './Text.scss';
import { IFieldElem } from '@models/IFormData';
import React, { useContext } from 'react';
import FieldWrapper from '@molecules/formbuilder/controls/FieldWrapper/FieldWrapper';
import { useActivated, useFormulaDisplay, useFormulaState } from '@molecules/formbuilder/hooks';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';
import Tooltip from '@srm/ui/tooltip';
import AutoLinkText from '@atoms/AutoLinkText';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { sanitizeDocumentHtml } from '@/utils/sanitizeDocumentHtml';
import { postProcessTextDisplay } from './postProcessTextDisplay';

export interface ITextProps {
    text?: IDocumentText;
    formMethods: UseFormReturn<any>;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const legacyBlackColors = new Set(['black', '#000', '#000000', 'rgb(0,0,0)', 'rgba(0,0,0,1)']);

const parseTextStyle = (value: unknown): React.CSSProperties => {
    if (typeof value !== 'string' || !value) {
        return {};
    }

    try {
        const style = JSON.parse(value.replace(/'/g, '"')) as React.CSSProperties;
        const color = typeof style.color === 'string' ? style.color.toLowerCase().replace(/\s/g, '') : '';
        const hasOwnBackground = style.background !== undefined || style.backgroundColor !== undefined;

        // В старых конфигурациях black означает обычный текст, но inline-цвет перекрывает тему.
        if (legacyBlackColors.has(color) && !hasOwnBackground) {
            return { ...style, color: 'var(--text-primary)' };
        }

        return style;
    } catch {
        return {};
    }
};

const Text = ({ text, formMethods, isEdit, isNew, fields, ensureExternalDataSourceLoaded }: ITextProps) => {
    const runtime = useContext(DocumentRuntimeContext);
    const readOnlyPreview = runtime.mode === 'supportPreview';
    const { activated } = useActivated();
    const visibility = useFormulaState(text?.visibilityRules, fields, formMethods, isEdit, isNew, activated, {
        fallback: true,
        ensureExternalDataSourceLoaded,
        trace: text?.id
            ? {
                  targetId: text.id,
                  targetKind: 'field',
                  targetName: text.name || text.id,
                  ruleKind: 'visibility',
              }
            : undefined,
    });
    const display = useFormulaDisplay(text?.value, fields, formMethods, isEdit, isNew, activated, {
        fallback: text?.value,
        postProcess: postProcessTextDisplay,
        ensureExternalDataSourceLoaded,
        trace: text?.id
            ? {
                  targetId: text.id,
                  targetKind: 'field',
                  targetName: text.name || text.id,
                  ruleKind: 'formula',
              }
            : undefined,
    });

    const style = parseTextStyle(text?.style);

    // У <text> без name заголовка нет, поэтому иконку-подсказку с viewTitle
    // выводить негде — в этом случае вешаем подсказку тултипом на само значение.
    const showValueTooltip = !text?.name && !!text?.viewTitle;

    const content = (
        <div
            className="content-text"
            style={showValueTooltip ? { ...style, cursor: 'help' } : style}
            dangerouslySetInnerHTML={{
                __html: sanitizeDocumentHtml(display.value?.toString() ?? '', readOnlyPreview),
            }}
        />
    );

    return text && text.value && visibility ? (
        <div
            className="form-field"
            data-testid={text.id ? `text-${text.id}` : undefined}
            {...formulaTraceAttrs(text.id, 'field')}
        >
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={!!text.name && isEmptyDisplayValue(display.value)}
                inLineFormat={false}
                label={text.name}
                title={text.viewTitle}
                testid={text.id}
            >
                {showValueTooltip ? (
                    <Tooltip
                        openDelay={100}
                        background="black"
                        position="bottom"
                        maxWidth="40ch"
                        useInnerHtml
                        label={readOnlyPreview ? text.viewTitle : <AutoLinkText text={text.viewTitle} />}
                    >
                        {content}
                    </Tooltip>
                ) : (
                    content
                )}
            </FieldWrapper>
        </div>
    ) : (
        <></>
    );
};

export default Text;
